import { CSS } from "@dnd-kit/utilities"
import React, { useCallback, useEffect, useRef } from "react"

import {
  MAX_COLUMN_WIDTH,
  MIN_COLUMN_WIDTH
} from "../EntityTable/entity-table.utils"
import { useReorderableHeader } from "../EntityTable/use-reorderable-header"

export interface ResizableHeaderCellProps
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  columnKey?: string
  reorderable?: boolean
  onColumnResize?: (key: string, width: number) => void
  onResizeStart?: () => void
  minWidth?: number
  maxWidth?: number
}

// Resize header cell with an optional dnd-kit reorder mode. When `reorderable`
// is set (and there is a `columnKey`), the cell is a sortable drag handle AND
// keeps its resize handle; it must be rendered inside a `SortableContext`.
// Without `reorderable` it stays a plain resize-only cell (no drag), and
// keyless / fixed cells fall through as static headers so AntD sticky stays intact.
export const ResizableHeaderCell: React.FC<ResizableHeaderCellProps> = ({
  columnKey,
  reorderable = false,
  onColumnResize,
  onResizeStart,
  minWidth = MIN_COLUMN_WIDTH,
  maxWidth = MAX_COLUMN_WIDTH,
  children,
  style,
  ...rest
}) => {
  const thRef = useRef<HTMLTableCellElement | null>(null)
  const resizable = !!columnKey && !!onColumnResize
  const isReorderable = reorderable && !!columnKey

  const { setNodeRef, dragProps, isDragging, transform, transition } =
    useReorderableHeader(columnKey, rest.onKeyDown, isReorderable)

  const setRef = useCallback(
    (node: HTMLTableCellElement | null) => {
      thRef.current = node
      if (isReorderable) setNodeRef(node)
    },
    [isReorderable, setNodeRef]
  )

  // If the cell unmounts mid-drag (the row it's in disappears, a filter
  // change swaps the table out, etc.) a pointerup may never fire to remove
  // the document-level listeners below — they'd otherwise leak for the rest
  // of the page's life, and a later stray pointer event would call
  // onColumnResize on unmounted state.
  const dragCleanupRef = useRef<(() => void) | null>(null)
  useEffect(() => () => dragCleanupRef.current?.(), [])

  const handleResizeStart = (event: React.PointerEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (!columnKey || !onColumnResize || !thRef.current) return
    const th = thRef.current
    onResizeStart?.()
    const startX = event.clientX
    const startWidth = th.getBoundingClientRect().width
    const onMove = (moveEvent: PointerEvent) => {
      const next = Math.min(
        maxWidth,
        Math.max(minWidth, Math.round(startWidth + moveEvent.clientX - startX))
      )
      onColumnResize(columnKey, next)
    }
    const onUp = () => {
      dragCleanupRef.current = null
      document.removeEventListener("pointermove", onMove)
      document.removeEventListener("pointerup", onUp)
      const suppressClick = (clickEvent: Event) => {
        clickEvent.stopPropagation()
        th.removeEventListener("click", suppressClick, true)
      }
      th.addEventListener("click", suppressClick, true)
      window.setTimeout(
        () => th.removeEventListener("click", suppressClick, true),
        0
      )
    }
    document.addEventListener("pointermove", onMove)
    document.addEventListener("pointerup", onUp)
    dragCleanupRef.current = () => {
      document.removeEventListener("pointermove", onMove)
      document.removeEventListener("pointerup", onUp)
    }
  }

  const mergedStyle: React.CSSProperties | undefined = isReorderable
    ? {
        ...style,
        position: style?.position ?? "relative",
        transform: CSS.Translate.toString(transform),
        transition,
        cursor: "grab",
        touchAction: "none",
        ...(isDragging ? { opacity: 0.6, zIndex: 2 } : {})
      }
    : resizable
      ? { ...style, position: style?.position ?? "relative" }
      : style

  return (
    <th
      ref={setRef}
      data-col-key={columnKey}
      style={mergedStyle}
      {...rest}
      {...dragProps}
    >
      {children}
      {resizable ? (
        <span
          className="entity-table-resize-handle"
          data-testid={`resize-handle-${columnKey}`}
          onPointerDown={handleResizeStart}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: 0,
            right: -4,
            height: "100%",
            width: 8,
            cursor: "col-resize",
            userSelect: "none",
            touchAction: "none",
            zIndex: 1
          }}
          aria-hidden="true"
        />
      ) : null}
    </th>
  )
}

export default ResizableHeaderCell
